import { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import { useSmartLinkActions } from '@atlaskit/smart-card/hook/use-smart-link-actions';
import { useSmartLinkReload } from '@atlaskit/smart-card/hook/use-smart-link-reload';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { ANALYTICS_SOURCE, CONNECTION_ROW_PREVIEW_ACTION_SUBJECT_ID } from '../analytics.ts';
import type { ConnectionRowAction } from '../utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

/**
 * Derives the "Open preview" row action for a connections row from the link's
 * SmartCard state.
 *
 * Called by `ConnectionsByLineMenuItem` for every row, which appends the result
 * to the actions its section supplied. Returns null unless the link is granted
 * and its SmartCard entry yields a `preview-content` action, so rows that can't
 * be previewed show no affordance. Requires a SmartCard provider in scope.
 */

const i18n = defineMessages({
	openPreview: {
		id: 'byline-connections.menu.item.open-preview',
		defaultMessage: 'Open preview',
		description:
			'Aria-label / tooltip for the button that opens an in-product preview of a connected connections menu item, shown on hover.',
	},
});

const PREVIEW_ACTION_ID = 'preview-content';

const reportPreviewActionError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
};

export const useSmartLinkPreviewAction = (
	item: EnhancedSmartLink,
	onPreviewOpen?: () => void,
): ConnectionRowAction | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasPreviewAccess = item.access === 'granted';
	const actions = useSmartLinkActions({
		url: item.url,
		appearance: 'inline',
		prefetch: hasPreviewAccess,
	});

	const previewAction = actions.find((action) => action.id === PREVIEW_ACTION_ID);

	// The platform only derives `preview-content` when the SmartCard entry carries
	// an embed `data.preview`. Same-site Confluence pages often resolve without
	// one — unlike Jira/3P links — leaving the row with no preview affordance. A
	// full reload makes ORS return the embed, after which the action appears.
	const reloadLink = useSmartLinkReload({ url: item.url });
	const hasRequestedPreviewResolveRef = useRef(false);

	// Reload on mount rather than on hover, so the icon is ready before the user
	// reaches the row, and at most once to avoid resolve loops. Deliberately not
	// gated on `actions.length`: a metadata-only entry yields zero actions, so
	// gating on it would block exactly the rows that need the reload.
	useEffect(() => {
		if (hasPreviewAccess && !previewAction && !hasRequestedPreviewResolveRef.current) {
			hasRequestedPreviewResolveRef.current = true;
			reloadLink();
		}
	}, [hasPreviewAccess, previewAction, reloadLink]);

	const handlePreviewInvoke = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: CONNECTION_ROW_PREVIEW_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: {
					product: item.product,
					resourceType: item.resourceType,
				},
			},
		}).fire();

		onPreviewOpen?.();
		void previewAction?.invoke().catch(reportPreviewActionError);
	}, [createAnalyticsEvent, item.product, item.resourceType, onPreviewOpen, previewAction]);

	const showPreviewAction = hasPreviewAccess && Boolean(previewAction);

	return useMemo(
		() =>
			showPreviewAction
				? {
						id: PREVIEW_ACTION_ID,
						icon: GrowDiagonalIcon,
						label: formatMessage(i18n.openPreview),
						onClick: handlePreviewInvoke,
					}
				: null,
		[showPreviewAction, formatMessage, handlePreviewInvoke],
	);
};
